import React, { useState, useEffect } from 'react';

import ActionButton from '../../Common/ActionButton';
import PanelBlock from '../../Common/PanelBlock';

import InputCurrencyField from '../../Fields/InputCurrencyField';
import Field from '../../Fields/Field';

import {
    queryWriteOffCredit,
    queryGetCreditBalances,
} from '../../../Queries';
import { creditWriteOffAmountValidator } from '../../../Validators/Credit';

import './CreditWriteOffPanel.scss';

const CreditWriteOffPanel = ({ credit, onExit }) => {
    const creditId = credit.fields.id.value;
    const applicationType = credit.fields.application_type.value;
    const isDNAC = applicationType === 'DO_NOT_APPLY_CASH';

    const defaultTax = isDNAC ? null : 0;

    const [amount, setAmount] = useState(0);
    const [tax1, setTax1] = useState(defaultTax);
    const [tax2, setTax2] = useState(defaultTax);
    const [tax3, setTax3] = useState(defaultTax);
    const [tax4, setTax4] = useState(defaultTax);

    const [amountIsValid, setAmountIsValid] = useState(true);
    const [tax1IsValid, setTax1IsValid] = useState(true);
    const [tax2IsValid, setTax2IsValid] = useState(true);
    const [tax3IsValid, setTax3IsValid] = useState(true);
    const [tax4IsValid, setTax4IsValid] = useState(true);

    const [creditBalances, setCreditBalances] = useState({});
    const [initialized, setInitialized] = useState(false);
    const [saveInProgress, setSaveInProgress] = useState(false);

    useEffect(() => {
        const getCreditBalances = async () => {
            try {
                const response = await queryGetCreditBalances({ creditId });
                if (response.errors) {
                    console.log(response.errors);
                    alert('Error loading credit write off panel');
                    onExit();
                }

                const balances = response.data.getCreditBalances;
                setCreditBalances(balances);
                setInitialized(true);
            } catch (error) {
                console.log(error);
                alert('Error loading credit write off panel');
                onExit();
            }
        };

        getCreditBalances();
    }, []);

    const getGrossAmount = () => (
        amount +
        (tax1 ?? 0) +
        (tax2 ?? 0) +
        (tax3 ?? 0) +
        (tax4 ?? 0)
    );

    const handleSave = async () => {
        setSaveInProgress(true);

        try {
            const response = await queryWriteOffCredit({
                creditId,
                amount: amount ?? 0,
                amountTax1: tax1 ?? defaultTax,
                amountTax2: tax2 ?? defaultTax,
                amountTax3: tax3 ?? defaultTax,
                amountTax4: tax4 ?? defaultTax,
            });

            if (response.errors) {
                console.log(response.errors);
                setSaveInProgress(false);
                alert('Error saving credit write off');
                return;
            }

            setSaveInProgress(false);
            onExit();
        } catch (error) {
            console.log(error);
            setSaveInProgress(false);
            alert('Error saving credit write off');
        }
    };

    const amountValidator = creditWriteOffAmountValidator(creditBalances.amount, setAmountIsValid);
    const tax1Validator = creditWriteOffAmountValidator(creditBalances.amount_tax_1, setTax1IsValid);
    const tax2Validator = creditWriteOffAmountValidator(creditBalances.amount_tax_2, setTax2IsValid);
    const tax3Validator = creditWriteOffAmountValidator(creditBalances.amount_tax_3, setTax3IsValid);
    const tax4Validator = creditWriteOffAmountValidator(creditBalances.amount_tax_4, setTax4IsValid);

    const errorText = (<span style={{ color: 'red' }}>Invalid Amount</span>);
    const grossAmount = getGrossAmount();

    const disableSave = !(
        amountIsValid &&
        tax1IsValid &&
        tax2IsValid &&
        tax3IsValid &&
        tax4IsValid &&
        (grossAmount > 0)
    );

    if (!initialized) return <></>;
    return (
        <PanelBlock
            heading='Write Off Credit'
            actions={[
                <ActionButton
                    id='parkchirp--write-off-credit-save-button'
                    label='Save'
                    onClick={handleSave}
                    inProgressState={saveInProgress}
                    disabled={disableSave}
                />,
                <button
                    type='button'
                    className='parkchirp--button-as-link'
                    style={{ paddingLeft: '10px', paddingRight: '10px' }}
                    onClick={onExit}
                >
                    Cancel
                </button>
            ]}
        >
            <div className='parkchirp--credit-write-off-panel--main-inputs-container'>
                <Field
                    label='Net Amount'
                    id='parkchirp--credit-write-off--net-amount'
                    containerClasses={['parkchirp--credit-write-off--input-block']}
                    layout='stacked'
                >
                    <InputCurrencyField
                        inputId={'amount'}
                        value={amount}
                        editMode={true}
                        onChange={setAmount}
                        validator={amountValidator}
                        validatorText={errorText}
                    />
                </Field>
                <Field
                    label='Tax 1'
                    id='parkchirp--credit-write-off--tax-1'
                    containerClasses={['parkchirp--credit-write-off--input-block']}
                    layout='stacked'
                >
                    <InputCurrencyField
                        inputId={'tax-1'}
                        value={tax1}
                        editMode={!isDNAC}
                        onChange={setTax1}
                        dashForNull={true}
                        validator={tax1Validator}
                        validatorText={errorText}
                    />
                </Field>
                <Field
                    label='Tax 2'
                    id='parkchirp--credit-write-off--tax-2'
                    containerClasses={['parkchirp--credit-write-off--input-block']}
                    layout='stacked'
                >
                    <InputCurrencyField
                        inputId={'tax-2'}
                        value={tax2}
                        editMode={!isDNAC}
                        onChange={setTax2}
                        dashForNull={true}
                        validator={tax2Validator}
                        validatorText={errorText}
                    />
                </Field>
                <Field
                    label='Tax 3'
                    id='parkchirp--credit-write-off--tax-3'
                    containerClasses={['parkchirp--credit-write-off--input-block']}
                    layout='stacked'
                >
                    <InputCurrencyField
                        inputId={'tax-3'}
                        value={tax3}
                        editMode={!isDNAC}
                        onChange={setTax3}
                        dashForNull={true}
                        validator={tax3Validator}
                        validatorText={errorText}
                    />
                </Field>
                <Field
                    label='Tax 4'
                    id='parkchirp--credit-write-off--tax-4'
                    containerClasses={['parkchirp--credit-write-off--input-block']}
                    layout='stacked'
                >
                    <InputCurrencyField
                        inputId={'tax-4'}
                        value={tax4}
                        editMode={!isDNAC}
                        onChange={setTax4}
                        dashForNull={true}
                        validator={tax4Validator}
                        validatorText={errorText}
                    />
                </Field>
                <Field
                    label='Gross Amount'
                    id='parkchirp--credit-write-off--gross-amount'
                    containerClasses={['parkchirp--credit-write-off--input-block']}
                    layout='stacked'
                >
                    <InputCurrencyField
                        inputId={'gross-amount'}
                        value={grossAmount}
                        editMode={false}
                    />
                </Field>
            </div>
        </PanelBlock>
    );
};

export default React.memo(CreditWriteOffPanel);
